import { useLayoutEffect, useSyncExternalStore } from 'react';
import type { AutomobileBrand, AutomobileCatalog } from '../types/automobile';
import {
  ensureAutomobileCatalogLoaded,
  getAutomobileCatalogState,
  isAutomobileCatalogReady,
  loadAutomobileCatalog,
  subscribeAutomobileCatalog,
} from '../utils/automobileCatalogStore';

const CATALOG_REFETCH_MS = 45_000;

/** İlk /otomobil ziyaretinde otomatik yükleme */
export function useAutomobileCatalogBootstrap(): void {
  useLayoutEffect(() => {
    ensureAutomobileCatalogLoaded();

    const onFocus = () => void loadAutomobileCatalog({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void loadAutomobileCatalog({ silent: true });
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadAutomobileCatalog({ silent: true });
    }, CATALOG_REFETCH_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(interval);
    };
  }, []);
}

export function useAutomobileCatalogData(): {
  catalog: AutomobileCatalog;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  useLayoutEffect(() => {
    ensureAutomobileCatalogLoaded();
  }, []);

  const snapshot = useSyncExternalStore(
    subscribeAutomobileCatalog,
    getAutomobileCatalogState,
    getAutomobileCatalogState,
  );

  return {
    catalog: snapshot.catalog,
    loading: snapshot.loading,
    error: snapshot.error,
    refresh: () => loadAutomobileCatalog({ silent: true }),
  };
}

export function useAutomobileBrandData(slug: string): {
  brand?: AutomobileBrand;
  loading: boolean;
  error: string | null;
  catalogReady: boolean;
} {
  const { catalog, loading, error } = useAutomobileCatalogData();
  const brand = slug ? catalog.brands.find((b) => b.slug === slug) : undefined;
  const catalogReady = isAutomobileCatalogReady();
  const awaitingCatalog = !catalogReady || loading;
  return { brand, loading: awaitingCatalog, error, catalogReady };
}
